import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Functions as FunctionsIcon,
  AccountTree as ChainIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  ErrorOutline as ErrorIcon,
  TipsAndUpdates as TipsIcon,
  Shield as ShieldIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWithPublicMode } from '../../utils/apiUtils';
import { useLearningAttention } from '../learning/common';
import { learningPath } from '../../services/learningApi';

const DAYS = 30;
const MAX_ATTENTION_ROWS = 5;

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

interface Overview {
  days: number;
  counts: { functions: number; chains: number; objects: number };
  activity: {
    runs: number;
    function_runs: number;
    chain_runs: number;
    failed: number;
    success_rate: number | null;
    avg_ms: number | null;
    p95_ms: number | null;
    rescued_by_reliability: number;
    daily: { date: string; runs: number; failed: number }[];
  };
  top_functions: {
    function_id: string | null;
    function_name: string;
    runs: number;
    success_rate: number | null;
    avg_ms: number | null;
    last_run: string | null;
    model: string | null;
  }[];
  recent_failures: { run_type: 'function' | 'chain'; id: string; name: string; created_at: string | null; error: string; model_used: string | null }[];
}

interface CreditUsage {
  total_credits_used: number;
  subscription_total_credits?: number | null;
  percentage_of_allowance_used?: number | null;
  period_start?: string;
}

const formatMs = (ms?: number | null) => {
  if (ms === undefined || ms === null) return '–';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
};

const timeAgo = (iso?: string | null) => {
  if (!iso) return '–';
  const s = Math.max(0, (Date.now() - new Date(iso.endsWith('Z') ? iso : `${iso}Z`).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const rateColor = (rate?: number | null): 'success' | 'warning' | 'error' | 'default' =>
  rate === null || rate === undefined ? 'default' : rate >= 95 ? 'success' : rate >= 80 ? 'warning' : 'error';

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  icon: React.ReactNode;
  loading: boolean;
  onClick?: () => void;
}> = ({ label, value, sub, icon, loading, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardActionArea onClick={onClick} disabled={!onClick} sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
              {loading ? <Skeleton width={70} /> : value}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ minHeight: 20 }}>
              {loading ? <Skeleton width={120} /> : sub}
            </Typography>
          </Box>
          <Box sx={{ color: 'primary.main', opacity: 0.8 }}>{icon}</Box>
        </Stack>
      </CardContent>
    </CardActionArea>
  </Card>
);

/** Account overview for the dashboard: what exists, how it ran, and what needs a look. */
export const DashboardOverview: React.FC<{ onCounts?: (counts: Overview['counts']) => void; onOpenChains: () => void }> = ({
  onCounts,
  onOpenChains,
}) => {
  const navigate = useNavigate();
  const { user, permissions, isPublicMode } = useAuth();
  const attention = useLearningAttention();
  const [data, setData] = useState<Overview | null>(null);
  const [credits, setCredits] = useState<CreditUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loading = !data && !error;

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      const idToken = await user.getIdToken();
      const isAdmin = permissions?.isAdmin || false;
      const headers = { Authorization: `Bearer ${idToken}` };
      const [overviewRes, creditsRes] = await Promise.allSettled([
        fetchWithPublicMode(`/api/v2/analytics/overview?days=${DAYS}`, { headers }, isPublicMode, isAdmin),
        fetchWithPublicMode('/api/v2/analytics/credit-usage', { headers }, isPublicMode, isAdmin),
      ]);
      if (!alive) return;
      if (overviewRes.status === 'fulfilled' && overviewRes.value.ok) {
        const body = await overviewRes.value.json();
        if (!alive) return;
        setData(body.data);
        onCounts?.(body.data.counts);
      } else {
        setError('Could not load your account overview.');
      }
      if (creditsRes.status === 'fulfilled' && creditsRes.value.ok) {
        const body = await creditsRes.value.json();
        if (alive) setCredits(body.data);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPublicMode, permissions?.isAdmin]);

  const attentionItems = Object.values(attention)
    .filter(i => i.open_suggestions > 0 || i.waiting_entries > 0)
    .sort((x, y) => y.open_suggestions - x.open_suggestions || y.waiting_entries - x.waiting_entries);
  const shownAttention = attentionItems.slice(0, MAX_ATTENTION_ROWS);
  const a = data?.activity;
  const chartData = (a?.daily || []).map(d => ({
    day: new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    Succeeded: d.runs - d.failed,
    Failed: d.failed,
  }));

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Overview
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Activity covers the last {DAYS} days
        </Typography>
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="AI Functions"
            value={data?.counts.functions.toLocaleString()}
            sub="Active, latest versions"
            icon={<FunctionsIcon sx={{ fontSize: 32 }} />}
            loading={loading}
            onClick={() => navigate('/dashboard/ai-functions-list')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Chains"
            value={data?.counts.chains.toLocaleString()}
            sub={a ? `${a.chain_runs.toLocaleString()} chain runs` : undefined}
            icon={<ChainIcon sx={{ fontSize: 32 }} />}
            loading={loading}
            onClick={onOpenChains}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Custom Objects"
            value={data?.counts.objects.toLocaleString()}
            sub="Data schemas for outputs"
            icon={<StorageIcon sx={{ fontSize: 32 }} />}
            loading={loading}
            onClick={() => navigate('/dashboard/objects')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label={`Runs (${DAYS} days)`}
            value={a?.runs.toLocaleString()}
            sub={
              a && a.runs ? (
                <>
                  {a.success_rate}% succeeded · {a.failed.toLocaleString()} failed
                </>
              ) : (
                'No runs yet'
              )
            }
            icon={<TimelineIcon sx={{ fontSize: 32 }} />}
            loading={loading}
            onClick={() => navigate('/dashboard/function-runs')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Activity
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Tooltip title="Average time for a function run to respond">
                  <Chip size="small" variant="outlined" label={`Avg ${formatMs(a?.avg_ms)}`} />
                </Tooltip>
                <Tooltip title="95% of function runs responded faster than this">
                  <Chip size="small" variant="outlined" label={`p95 ${formatMs(a?.p95_ms)}`} />
                </Tooltip>
                {!!a?.rescued_by_reliability && (
                  <Tooltip title="Runs that succeeded only because a retry or backup model took over after the first attempt failed">
                    <Chip
                      size="small"
                      color="info"
                      variant="outlined"
                      icon={<ShieldIcon />}
                      label={`${a.rescued_by_reliability.toLocaleString()} saved by reliability`}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : a && a.runs ? (
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={16} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <ChartTooltip />
                    <Bar dataKey="Succeeded" stackId="runs" fill="#667eea" />
                    <Bar dataKey="Failed" stackId="runs" fill="#e57373" />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">No runs in the last {DAYS} days. Run a function to see activity here.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                AI credits this period
              </Typography>
              {credits ? (
                <>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {credits.total_credits_used.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    {credits.subscription_total_credits ? (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {' '}
                        / {credits.subscription_total_credits.toLocaleString()}
                      </Typography>
                    ) : null}
                  </Typography>
                  {credits.percentage_of_allowance_used !== null && credits.percentage_of_allowance_used !== undefined && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, credits.percentage_of_allowance_used)}
                      color={credits.percentage_of_allowance_used >= 90 ? 'error' : credits.percentage_of_allowance_used >= 75 ? 'warning' : 'primary'}
                      sx={{ mt: 1, height: 6, borderRadius: 3 }}
                    />
                  )}
                  {credits.period_start && (
                    <Typography variant="caption" color="text.secondary">
                      Since {new Date(credits.period_start).toLocaleDateString()}
                    </Typography>
                  )}
                </>
              ) : (
                <Skeleton width={120} height={36} />
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Needs attention
              </Typography>
              {loading ? (
                <Skeleton height={80} />
              ) : (
                <Stack spacing={1}>
                  {shownAttention.map(i => (
                    <Box key={`${i.target_type}:${i.target_id}`}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TipsIcon color="warning" sx={{ fontSize: 16 }} />
                        <Link
                          component="button"
                          underline="hover"
                          onClick={() => navigate(learningPath(i.target_type, i.target_id, i.open_suggestions ? 'suggestions' : 'knowledge'))}
                          sx={{ fontWeight: 500, textAlign: 'left' }}
                        >
                          {i.target_type === 'chain' ? 'Chain: ' : ''}{i.target_name || (i.target_type === 'chain' ? 'Unnamed chain' : 'Unnamed function')}
                        </Link>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 3 }}>
                        Self-improvement: {[
                          i.open_suggestions ? `${plural(i.open_suggestions, 'suggestion')} to review` : '',
                          i.waiting_entries ? `${plural(i.waiting_entries, 'learning')} waiting` : '',
                        ].filter(Boolean).join(', ')}
                      </Typography>
                    </Box>
                  ))}
                  {attentionItems.length > shownAttention.length && (
                    <Typography variant="caption" color="text.secondary">
                      +{attentionItems.length - shownAttention.length} more: look for the badges in the Function Library and Chains.
                    </Typography>
                  )}
                  {data?.recent_failures.length ? (
                    data.recent_failures.map(f => (
                      <Box key={f.id}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <ErrorIcon color="error" sx={{ fontSize: 16 }} />
                          <Link
                            component="button"
                            underline="hover"
                            onClick={() => navigate(f.run_type === 'chain' ? `/dashboard/chain-run/${f.id}` : `/dashboard/run/${f.id}`)}
                            sx={{ fontWeight: 500, textAlign: 'left' }}
                          >
                            {f.run_type === 'chain' ? `Chain: ${f.name}` : f.name}
                          </Link>
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
                            {timeAgo(f.created_at)}
                          </Typography>
                        </Stack>
                        {f.error && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {f.error}
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : attentionItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Nothing right now: no failed runs in the last {DAYS} days.
                    </Typography>
                  ) : null}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Most used functions
          </Typography>
          <Button size="small" endIcon={<ArrowIcon />} onClick={() => navigate('/dashboard/function-runs')}>
            Usage history
          </Button>
        </Stack>
        {loading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : data?.top_functions.length ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Function</TableCell>
                <TableCell align="right">Runs</TableCell>
                <TableCell align="right">Success</TableCell>
                <TableCell align="right">Avg time</TableCell>
                <TableCell>Usual model</TableCell>
                <TableCell align="right">Last run</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.top_functions.map(f => (
                <TableRow
                  key={f.function_name}
                  hover
                  sx={{ cursor: f.function_id ? 'pointer' : undefined }}
                  onClick={() => f.function_id && navigate(`/dashboard/function-runs/${f.function_id}`)}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{f.function_name}</TableCell>
                  <TableCell align="right">{f.runs.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Chip size="small" color={rateColor(f.success_rate)} label={f.success_rate === null ? '–' : `${f.success_rate}%`} />
                  </TableCell>
                  <TableCell align="right">{formatMs(f.avg_ms)}</TableCell>
                  <TableCell>{f.model ? <Chip size="small" variant="outlined" label={f.model} /> : '–'}</TableCell>
                  <TableCell align="right">{timeAgo(f.last_run)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No function runs in the last {DAYS} days.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardOverview;
